import { Switch, ToggleGroup } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';

export function Tarjeta({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn('rounded-tarjeta border border-border bg-surface shadow-tarjeta', className)}
      {...props}
    />
  );
}

export function TituloTarjeta({
  children,
  accion,
  className,
}: {
  children: ReactNode;
  accion?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-h-6 items-center gap-3', className)}>
      <h2 className="flex-1 text-[15px] font-bold text-text">{children}</h2>
      {accion}
    </div>
  );
}

export function Esqueleto({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulso-suave rounded-[8px] bg-surface-muted', className)}
      {...props}
    />
  );
}

/** Empty state: says what is missing and what to do next, never just "No data". */
export function Vacio({
  icono,
  titulo,
  children,
  accion,
  className,
  compacto,
}: {
  icono?: ReactNode;
  titulo: ReactNode;
  children?: ReactNode;
  accion?: ReactNode;
  className?: string;
  compacto?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compacto ? 'gap-1.5 px-4 py-6' : 'gap-2 px-6 py-14',
        className,
      )}
    >
      {icono && (
        <div
          className={cn(
            'mb-1 grid place-items-center rounded-[14px] bg-surface-muted text-text-2 [&_svg]:size-6',
            compacto ? 'size-10' : 'size-12',
          )}
        >
          {icono}
        </div>
      )}
      <p className={cn('font-bold text-text', compacto ? 'text-[14px]' : 'text-[16px]')}>
        {titulo}
      </p>
      {children && (
        <p className={cn('max-w-[46ch] text-text-2', compacto ? 'text-[13px]' : 'text-[14px]')}>
          {children}
        </p>
      )}
      {accion && <div className="mt-3">{accion}</div>}
    </div>
  );
}

export function ErrorCarga({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  const permiso = /permission|insufficient/i.test(msg);
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-tarjeta border border-que-strong/40 bg-que-soft px-6 py-8 text-center"
    >
      <p className="font-bold text-que-texto">
        {permiso ? 'No tienes permiso para ver esto' : 'No pudimos cargar los datos'}
      </p>
      <p className="max-w-[52ch] text-[14px] text-que-texto/80">
        {permiso
          ? 'Tu rol no tiene acceso a esta sección. Si crees que es un error, habla con el administrador del taller.'
          : 'Revisa tu conexión. Los datos se actualizan solos apenas vuelva la conexión.'}
      </p>
      {reintentar && !permiso && (
        <button
          type="button"
          onClick={reintentar}
          className="mt-1 text-[14px] font-bold text-que-texto underline underline-offset-4"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}

/** Segmented control (handoff: 52px on tablet, 36-42px on desktop). */
export function Segmentado<T extends string>({
  valor,
  onCambio,
  opciones,
  etiqueta,
  className,
  tamano = 'md',
}: {
  valor: T;
  onCambio: (v: T) => void;
  opciones: readonly { valor: T; texto: ReactNode; cantidad?: number }[];
  etiqueta: string;
  className?: string;
  tamano?: 'sm' | 'md';
}) {
  return (
    <ToggleGroup.Root
      type="single"
      value={valor}
      onValueChange={(v) => v && onCambio(v as T)}
      aria-label={etiqueta}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-[11px] border border-border bg-surface-muted p-[3px]',
        className,
      )}
    >
      {opciones.map((o) => (
        <ToggleGroup.Item
          key={o.valor}
          value={o.valor}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[8px] px-3 whitespace-nowrap text-text-long transition-[background-color,color,box-shadow] duration-150 ease-out',
            tamano === 'sm' ? 'h-7 text-[13px]' : 'h-[34px] text-[14px]',
            'hover:text-text',
            'data-[state=on]:bg-surface data-[state=on]:font-bold data-[state=on]:text-text data-[state=on]:shadow-[0_1px_2px_rgb(26_23_24/0.1),0_0_0_1px_var(--tm-border)]',
          )}
        >
          {o.texto}
          {o.cantidad !== undefined && (
            <span className="tabular text-[12px] font-normal text-text-2">{o.cantidad}</span>
          )}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

export function Interruptor({ className, ...props }: ComponentProps<typeof Switch.Root>) {
  return (
    <Switch.Root
      className={cn(
        'relative inline-flex h-6 w-10 flex-none items-center rounded-full border border-transparent bg-border-input transition-colors duration-150',
        'data-[state=checked]:bg-primary',
        className,
      )}
      {...props}
    >
      <Switch.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/0.25)] transition-transform duration-200 ease-[var(--ease-salida)] data-[state=checked]:translate-x-[18px]" />
    </Switch.Root>
  );
}

export type TonoInsignia = 'neutro' | 'primario' | 'exito' | 'alerta' | 'peligro' | 'acento';

const tonos: Record<TonoInsignia, string> = {
  neutro: 'bg-surface-muted text-text-long border-border',
  primario: 'bg-primary-soft text-primary-text border-primary/25',
  exito: 'bg-sin-soft text-sin-texto border-sin-strong/35',
  alerta: 'bg-ray-soft text-ray-texto border-ray-strong/40',
  peligro: 'bg-que-soft text-que-texto border-que-strong/35',
  acento:
    // The brand accent is a mark color, not a text color: mix it toward the
    // text color (lighter in dark mode) so the badge reads AA in both themes.
    'bg-[color-mix(in_oklab,var(--tm-acento)_12%,var(--tm-surface))] text-[color-mix(in_oklab,var(--tm-acento)_60%,var(--tm-text))] border-acento/35',
};

export function Insignia({
  tono = 'neutro',
  className,
  punto,
  children,
}: {
  tono?: TonoInsignia;
  className?: string;
  punto?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-[12.5px] leading-none font-bold whitespace-nowrap',
        tonos[tono],
        className,
      )}
    >
      {punto && <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Tecla({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-border bg-surface px-1 font-sans text-[11px] font-bold text-text-2 shadow-[0_1px_0_var(--tm-border)]',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

/** Horizontal occupancy bar (board columns, summary). */
export function BarraCupo({
  usados,
  total,
  className,
  grosor = 5,
}: {
  usados: number;
  total: number;
  className?: string;
  grosor?: number;
}) {
  const lleno = usados >= total;
  const pct = total > 0 ? Math.min(100, (usados / total) * 100) : 0;
  return (
    <div
      className={cn('flex-1 overflow-hidden rounded-full bg-cupo-pista', className)}
      style={{ height: grosor }}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={usados}
      aria-label={`${usados} de ${total} cupos${lleno ? ', lleno' : ''}`}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width,background-color] duration-300 ease-[var(--ease-salida)]',
          lleno ? 'bg-ray-strong' : 'bg-primary',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
